/**
 * TeamDetailDrawer - Full team info drawer with actions (DRR-061)
 *
 * Shows all team fields organized by category.
 * Action buttons: Edit, Archive, Manage Members.
 * Dumb Frontend: displays backend data only, no hardcoded logic.
 */

import {
  Drawer,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Divider,
  Button,
  SimpleGrid,
  Paper,
  Table,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import {
  IconEdit,
  IconArchive,
  IconUsers,
  IconCalendar,
  IconMail,
  IconBrandSlack,
  IconLink,
  IconArrowsMove,
  IconGauge,
} from '@tabler/icons-react';
import type { GqlTeam } from '@ferp/workflow-forms';

const TEAM_TYPE_COLORS: Record<string, string> = {
  SQUAD: 'cyan',
  GROUP: 'green',
  DEPARTMENT: 'blue',
  DIVISION: 'purple',
  PROJECT: 'orange',
  CUSTOM: 'gray',
};

interface TeamDetailDrawerProps {
  team: GqlTeam | null;
  opened: boolean;
  onClose: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onManageMembers: () => void;
  onMoveTeam?: () => void;
  onUpdateQuotas?: () => void;
}

function InfoRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null || value === '') return null;
  return (
    <Group gap="xs">
      <Text size="xs" c="dimmed" w={120}>{label}</Text>
      <Text size="sm">{String(value)}</Text>
    </Group>
  );
}

export function TeamDetailDrawer({
  team,
  opened,
  onClose,
  onEdit,
  onArchive,
  onManageMembers,
  onMoveTeam,
  onUpdateQuotas,
}: TeamDetailDrawerProps) {
  if (!team) return null;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="sm">
          <Title order={4}>{team.name}</Title>
          <Badge color={TEAM_TYPE_COLORS[team.teamType] ?? 'gray'} variant="light" size="sm">
            {team.teamType}
          </Badge>
          {!team.active && (
            <Badge color="red" variant="light" size="sm">Archived</Badge>
          )}
        </Group>
      }
      size="lg"
      position="right"
    >
      <Stack gap="md">
        {/* Actions */}
        <Group gap="xs" wrap="wrap">
          <Button size="xs" variant="light" leftSection={<IconEdit size={14} />} onClick={onEdit}>
            Edit
          </Button>
          <Button size="xs" variant="light" leftSection={<IconUsers size={14} />} onClick={onManageMembers}>
            Members ({team.stakeholderIds?.length ?? 0})
          </Button>
          {onMoveTeam && (
            <Button size="xs" variant="light" color="violet" leftSection={<IconArrowsMove size={14} />} onClick={onMoveTeam}>
              Move
            </Button>
          )}
          {onUpdateQuotas && (
            <Button size="xs" variant="light" color="teal" leftSection={<IconGauge size={14} />} onClick={onUpdateQuotas}>
              Quotas
            </Button>
          )}
          {team.active && (
            <Button size="xs" variant="light" color="red" leftSection={<IconArchive size={14} />} onClick={onArchive}>
              Archive
            </Button>
          )}
        </Group>

        <Divider />

        {/* Identity */}
        <Paper p="sm" withBorder>
          <Text size="xs" fw={600} c="dimmed" mb="xs">Identity</Text>
          <InfoRow label="Code" value={team.code} />
          <InfoRow label="Description" value={team.description} />
          {team.tags?.length > 0 && (
            <Group gap="xs" mt="xs">
              <Text size="xs" c="dimmed" w={120}>Tags</Text>
              {team.tags.map((tag) => (
                <Badge key={tag} size="xs" variant="outline">{tag}</Badge>
              ))}
            </Group>
          )}
        </Paper>

        {/* Hierarchy */}
        <Paper p="sm" withBorder>
          <Text size="xs" fw={600} c="dimmed" mb="xs">Hierarchy</Text>
          <InfoRow label="Parent Team" value={team.parentTeamId ?? '(Root)'} />
          <InfoRow label="Level" value={team.level} />
          <InfoRow label="Path" value={team.path} />
        </Paper>

        {/* Leadership */}
        <Paper p="sm" withBorder>
          <Text size="xs" fw={600} c="dimmed" mb="xs">Leadership</Text>
          <InfoRow label="Team Lead" value={team.teamLeadId ?? 'Not assigned'} />
          <InfoRow label="Deputy Lead" value={team.deputyLeadId ?? 'Not assigned'} />
          <InfoRow label="Stakeholders" value={`${team.stakeholderIds?.length ?? 0} members`} />
        </Paper>

        {/* Resources */}
        <Paper p="sm" withBorder>
          <Text size="xs" fw={600} c="dimmed" mb="xs">Resources</Text>
          <InfoRow label="Budget" value={team.budgetAllocated != null ? `$${team.budgetAllocated.toLocaleString()}` : 'Not set'} />
          <InfoRow label="Cost Center" value={team.costCenter} />
          {team.quotaLimits && (
            <>
              <InfoRow label="Max Members" value={team.quotaLimits.maxMembers} />
              <InfoRow label="Max Workflows" value={team.quotaLimits.maxWorkflows} />
            </>
          )}
        </Paper>

        {/* Contacts */}
        {team.contacts && (team.contacts.email || team.contacts.chatChannel || team.contacts.wikiUrl || team.contacts.calendarUrl) && (
          <Paper p="sm" withBorder>
            <Text size="xs" fw={600} c="dimmed" mb="xs">Contacts</Text>
            <InfoRow label="Email" value={team.contacts.email} />
            <InfoRow label="Chat Channel" value={team.contacts.chatChannel} />
            <InfoRow label="Wiki" value={team.contacts.wikiUrl} />
            <InfoRow label="Calendar" value={team.contacts.calendarUrl} />
          </Paper>
        )}

        {/* Lifecycle */}
        <Paper p="sm" withBorder>
          <Text size="xs" fw={600} c="dimmed" mb="xs">Lifecycle</Text>
          <InfoRow label="Effective Date" value={team.effectiveDate ? new Date(team.effectiveDate).toLocaleDateString() : 'Not set'} />
          <InfoRow label="End Date" value={team.endDate ? new Date(team.endDate).toLocaleDateString() : undefined} />
          <InfoRow label="Status Reason" value={team.statusReason} />
        </Paper>

        {/* Audit */}
        <Paper p="sm" withBorder>
          <Text size="xs" fw={600} c="dimmed" mb="xs">Audit Trail</Text>
          <InfoRow label="Created" value={new Date(team.createdAt).toLocaleString()} />
          <InfoRow label="Created By" value={team.createdBy} />
          <InfoRow label="Updated" value={new Date(team.updatedAt).toLocaleString()} />
          <InfoRow label="Updated By" value={team.updatedBy} />
          <InfoRow label="Version" value={team.auditVersion} />
        </Paper>
      </Stack>
    </Drawer>
  );
}
